import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Star, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Empty } from "@/components/Shared";

export default function Compare() {
  const [params] = useSearchParams();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  const ids = (params.get("ids") || "").split(",").filter(Boolean).slice(0, 3);

  useEffect(() => {
    if (!ids.length) { setLoading(false); return; }
    Promise.all(ids.map((id) => api.get(`/tools/${id}`).then((r) => r.data).catch(() => null)))
      .then((res) => setTools(res.filter(Boolean)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);

  if (loading) return <Empty text="Loading comparison…" />;
  if (!tools.length) return <Empty text="No tools selected to compare." />;

  const rows = [
    { label: "Company", get: (t) => t.company || t.company_name || "—" },
    { label: "Category", get: (t) => t.category },
    { label: "Rating", get: (t) => t.rating ? <span className="flex items-center gap-1 text-[#F59E0B]"><Star className="w-3.5 h-3.5 fill-[#F59E0B]" /> {t.rating}</span> : "—" },
    { label: "Users", get: (t) => t.users || "—" },
    { label: "Pricing", get: (t) => t.pricing_model || "—" },
    { label: "Availability", get: (t) => t.availability === "coming_soon" ? <span className="text-[#F59E0B]">Coming soon</span> : <span className="flex items-center gap-1 text-[#10B981]"><Check className="w-3.5 h-3.5" /> Live</span> },
    { label: "Badge", get: (t) => t.badge || "—" },
    { label: "Tags", get: (t) => (t.tags || []).length ? <div className="flex flex-wrap gap-1">{t.tags.map((x) => <span key={x} className="font-mono-tech text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5">{x}</span>)}</div> : "—" },
    { label: "Description", get: (t) => <span className="text-[#94A3B8]">{t.description}</span> },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="compare-page">
      <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#00F0FF] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Marketplace
      </Link>
      <h1 className="font-display font-extrabold text-4xl mb-8">Compare Tools</h1>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[640px]">
          <thead>
            <tr>
              <th className="text-left p-4 w-40 align-bottom"></th>
              {tools.map((t) => (
                <th key={t.id} className="text-left p-4 align-bottom border-b border-white/10" data-testid={`compare-col-${t.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center font-display font-bold text-[#00F0FF] overflow-hidden mb-2">
                    {t.logo_url ? <img src={t.logo_url} alt={t.name} className="w-full h-full object-cover" /> : (t.name[0] || "?")}
                  </div>
                  <Link to={`/tools/${t.id}`} className="font-display font-bold text-lg hover:text-[#00F0FF]">{t.name}</Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b border-white/5">
                <td className="p-4 font-mono-tech text-xs uppercase text-[#94A3B8] align-top">{row.label}</td>
                {tools.map((t) => (
                  <td key={t.id} className="p-4 text-sm align-top">{row.get(t)}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="p-4"></td>
              {tools.map((t) => (
                <td key={t.id} className="p-4">
                  <a href={t.website} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition">
                    Visit <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
